import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCanEdit } from '@/features/auth/AuthContext';
import { useClearDemo, useDeleteGuia, useGuias, useMonthSummary } from '@/features/guias/hooks';
import { exportAnnual, exportCsv, ocrGuia } from '@/features/guias/api';
import { ApiError } from '@/lib/apiClient';
import { currentMonth, shiftMonth } from '@/lib/format';
import type { GuiaDto } from '@/types';
import { MonthNavigator } from '@/components/MonthNavigator';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { DashboardActions } from './DashboardActions';
import { ResumoTab } from './ResumoTab';
import { GuiasTab } from './GuiasTab';

type Tab = 'resumo' | 'guias';

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canEdit = useCanEdit();

  const [params, setParams] = useSearchParams();
  const mes = params.get('mes') ?? currentMonth();
  const [tab, setTab] = useState<Tab>('resumo');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guiasQuery = useGuias(mes);
  const summaryQuery = useMonthSummary(mes);
  const deleteGuia = useDeleteGuia();
  const clearDemo = useClearDemo();

  const setMes = (value: string) =>
    setParams((prev) => {
      prev.set('mes', value);
      return prev;
    });

  const count = guiasQuery.data?.length ?? 0;
  const hasDemo = guiasQuery.data?.some((g) => g.demo) ?? false;

  async function handleFile(file: File) {
    setProcessing(true);
    setError(null);
    try {
      const ocr = await ocrGuia(file);
      navigate('/guias/novo', { state: { ocr } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.network'));
    } finally {
      setProcessing(false);
    }
  }

  function downloadBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleExport() {
    try {
      const { blob, fileName } = await exportCsv(mes);
      downloadBlob(blob, fileName);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.network'));
    }
  }

  async function handleExportAnnual() {
    try {
      const year = Number(mes.slice(0, 4));
      const { blob, fileName } = await exportAnnual(year);
      downloadBlob(blob, fileName);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.network'));
    }
  }

  function handleEdit(guia: GuiaDto) {
    navigate(`/guias/${guia.id}`, { state: { guia } });
  }

  function handleDelete(guia: GuiaDto) {
    if (window.confirm(t('guias.confirmDelete'))) deleteGuia.mutate(guia.id);
  }

  const tabClass = (active: boolean) =>
    `rounded-lg px-5 py-2 text-sm font-semibold transition ${
      active ? 'bg-conduril text-white shadow-btn' : 'text-conduril-muted hover:text-conduril-ink'
    }`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full sm:w-72">
          <MonthNavigator
            mes={mes}
            onChange={setMes}
            onPrev={() => setMes(shiftMonth(mes, -1))}
            onNext={() => setMes(shiftMonth(mes, 1))}
          />
        </div>
        <span className="text-sm font-medium text-conduril-muted">{t('header.guias', { count })}</span>
      </div>

      {canEdit && (
        <DashboardActions
          busy={processing}
          onFile={handleFile}
          onManual={() => navigate('/guias/novo')}
          onExport={handleExport}
          onExportAnnual={handleExportAnnual}
          onManageObras={() => navigate('/obras')}
          year={mes.slice(0, 4)}
        />
      )}

      {processing && (
        <div className="flex items-center gap-2.5 rounded-xl border border-[#e0d090] bg-[#fff8dc] px-3.5 py-3 text-sm">
          <Spinner />
          {t('dashboard.reading')}
        </div>
      )}
      <ErrorBanner message={error} />

      <div className="inline-flex w-full gap-1 self-start rounded-xl border border-conduril-border bg-white p-1 shadow-card sm:w-auto">
        <button type="button" onClick={() => setTab('resumo')} className={`flex-1 sm:flex-none ${tabClass(tab === 'resumo')}`}>
          {t('dashboard.tabResumo')}
        </button>
        <button type="button" onClick={() => setTab('guias')} className={`flex-1 sm:flex-none ${tabClass(tab === 'guias')}`}>
          {t('dashboard.tabGuias')}
        </button>
      </div>

      {tab === 'resumo' ? (
        <ResumoTab summary={summaryQuery.data} loading={summaryQuery.isLoading} />
      ) : (
        <GuiasTab
          guias={guiasQuery.data}
          loading={guiasQuery.isLoading}
          canEdit={canEdit}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {canEdit && hasDemo && (
        <button
          type="button"
          onClick={() => clearDemo.mutate()}
          className="p-2 text-[13px] text-conduril-muted underline"
        >
          {t('dashboard.clearDemo')}
        </button>
      )}
    </div>
  );
}
